import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import moment from 'moment';
import { formatNumber, jsonToArray, loader } from 'helpers/generic';
import { getCommunity } from 'helpers/community';

let axiosCancelToken = null;

const CounterpartsStyled = styled.div`
    padding: 20px;

    h1 {
        text-align: center;
        font-size: 25px;
        display: block;
        margin-bottom: 20px;
    }

    h2 {
        text-align: right;
        font-size: 18px;

        b {
            font-weight: 600;
        }
    }

    table#movements {
        font-size: 13px;

        td {
            &:nth-child(2),
            &:nth-child(3),
            &:nth-child(4) {
                width: 50px;
                text-align: right;
                white-space: nowrap;
            }
        }

        tbody, thead, tfoot {
            border-top-color: #dee2e6;
        }

        thead {
            &:first-of-type {
                background: var(--bs-primary);
                color: white;
            }
        }

        tfoot {
            font-weight: 600;
            background: var(--bs-gray-200);
        }

        tr {
            &.subtotal {
                font-weight: 600;
                background: var(--bs-gray-100);


                &:not(:last-of-type) {
                    
                }

                td {
                    text-align: right;
                }
            }

            &.bg-orange {
                background: rgba(var(--bs-orange-rgb), 0.5);
            }  
        }
    }
`;

export default function Counterparts(props) {
    const year = props.year;
    const setExportBtn = props.setExportBtn;

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [cacheInfo, setCacheInfo] = useState(null);
    const [jobInfo, setJobInfo] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Estado para detectar si hay una nueva versión del balance
    const [hasNewVersion, setHasNewVersion] = useState(false);
    const lastGeneratedAtRef = useRef(null); // Usamos ref persistente

    // Modificado: fetchCache acepta un flag para no mostrar loading (por ejemplo, en polling)
    const fetchCache = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        setError(null);
        setData(null);
        setCacheInfo(null);
        setJobInfo(null);
        try {
            const res = await axios.get('/api/manager/balances/cached', {
                params: {
                    community_id: getCommunity()?.id,
                    type: 'counterparts',
                    financial_year: year
                }
            });
            setCacheInfo(res.data.cache);
            setJobInfo(res.data.job);
            setData(res.data.cache?.data || null);
            // Actualizamos la ref con el generated_at actual
            lastGeneratedAtRef.current = res.data.cache?.generated_at || null;
        } catch (e) {
            setError('No se pudo obtener el balance.');
        }
        if (showLoading) setLoading(false);
    }, [year]);

    const handleRequestJob = async () => {
        setRefreshing(true);
        setError(null);
        try {
            await axios.post('/api/manager/balances/request-job', {
                community_id: getCommunity()?.id,
                type: 'counterparts',
                financial_year: year
            });
            fetchCache();
        } catch (e) {
            if (e.response && e.response.status === 409) {
                setError('Ya hay un balance en proceso para este año.');
            } else {
                setError('Error al solicitar la generación del balance.');
            }
        }
        setRefreshing(false);
    };

    // Controla si ya se hizo el fetch inicial para evitar doble request-job
    const [fetched, setFetched] = useState(false);
    const pollingRef = useRef(false);

    // Fetch inicial sólo una vez (ignora StrictMode doble render)
    useEffect(() => {
        let ignore = false;
        if (!fetched && !ignore) {
            setFetched(true);
            fetchCache();
        }
        return () => { ignore = true; };
    }, [fetched, fetchCache]);

    // Polling cada 5s si hay un job en proceso, pero sólo para detectar nueva versión
    useEffect(() => {
        let interval = null;
        if (jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing')) {
            pollingRef.current = true;
            interval = setInterval(async () => {
                try {
                    const res = await axios.get('/api/manager/balances/cached', {
                        params: {
                            community_id: getCommunity()?.id,
                            type: 'counterparts',
                            financial_year: year
                        }
                    });
                    
                    // Si el job cambió a done y hay cache más reciente
                    if (res.data.job?.status === 'done' && 
                        res.data.cache?.generated_at && 
                        res.data.cache.generated_at !== lastGeneratedAtRef.current) {
                        setHasNewVersion(true);
                        setJobInfo(res.data.job);
                        setCacheInfo(res.data.cache);
                    }
                } catch (e) {
                    console.error('Error en polling:', e);
                }
            }, 5000);
        } else {
            pollingRef.current = false;
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [jobInfo, cacheInfo, year]);

    // Handler para recargar los datos manualmente
    const handleReload = async () => {
        setHasNewVersion(false);
        await fetchCache();
    }

    // Si hay un job en proceso, ocultar el aviso de nueva versión
    useEffect(() => {
        if (jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing')) {
            setHasNewVersion(false);
        }
    }, [jobInfo]);

    useEffect(() => {
        if (data !== null && !loading) {
            setExportBtn(<ExportBtn year={year} />);
        } else {
            setExportBtn(null);
        }
    }, [year, data, loading, setExportBtn]);

    useEffect(() => {
        axiosCancelToken = axios.CancelToken.source();

        return function cleanup() {
            axiosCancelToken.cancel();
        }
    }, [year]);

    return (
        <>
            {loading && <div style={{textAlign: 'center'}}>{loader}</div>}

            {error ? (
                <div className="alert alert-danger m-3" role="alert">{error}</div>
            ) :
            (!hasNewVersion && !cacheInfo && !loading && !(jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing'))) && (
                <div className="alert alert-warning m-3" role="alert">No hay balance en cache para este año.</div>
            )}

            {cacheInfo && cacheInfo.generated_at && (
                <div className="alert alert-info m-3" role="alert">
                    Balance generado el: {new Date(cacheInfo.generated_at).toLocaleString()}
                </div>
            )}

            {/* Mostrar aviso de nueva versión solo si hay una nueva versión Y no hay job en proceso */}
            {hasNewVersion && (!jobInfo || (jobInfo.status !== 'pending' && jobInfo.status !== 'processing')) && (
                <div className="alert alert-success m-3 d-flex align-items-center justify-content-between" role="alert">
                    <span>Hay una nueva versión del balance disponible.</span>
                    <button className="btn btn-sm btn-success text-white ms-3" onClick={handleReload}>Recargar datos</button>
                </div>
            )}

            {/* Mostrar botón de generar solo si NO hay job en proceso y NO hay nueva versión pendiente de recarga */}
            {!hasNewVersion && (!jobInfo || (jobInfo.status !== 'pending' && jobInfo.status !== 'processing')) && (
                <button className="btn btn-primary text-white m-3 my-0" onClick={handleRequestJob} disabled={refreshing}>
                    {refreshing ? 'Solicitando...' : 'Generar/Recalcular balance'}
                </button>
            )}

            {/* Mostrar aviso de proceso solo si NO hay nueva versión pendiente de recarga y hay job en proceso */}
            {!hasNewVersion && jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing') && (
                <div className="alert alert-warning m-3" role="alert">Hay un balance en proceso para este año. Por favor, espera a que termine.</div>
            )}
                       
            {(data !== null && !loading) && 
                <CounterpartsStyled>
                    <h1 className="title">BALANCE DE CONTRAPARTIDAS {year}</h1>

                    <table id="movements" className="table table-sm table-bordered">
                        <thead>
                            <tr>
                                <th>GASTOS POR CUENTAS</th>
                                <th>IMPORTE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jsonToArray(data?.expenses_detailed)?.map(estimate => {

                                return (
                                    <React.Fragment key={'g-estimate-'+estimate.key}>
                                        <tr key={'g-estimate-'+estimate.key} style={{background: 'var(--bs-gray-400)'}} className="fw-bold">
                                            <td>{estimate.value.name}</td>
                                            <td>{formatNumber(estimate.value.amount ?? 0, 2)} €</td>
                                        </tr>
                                        {jsonToArray(estimate?.value.groups)?.map(group => {
                                            let distributionType = null;
                                            if ( group.value.distribution_type === 'coefficient' ) distributionType = 'Coeficiente';
                                            if ( group.value.distribution_type === 'equal' ) distributionType = 'Partes iguales';
                                            if ( group.value.distribution_type === 'consumption' ) distributionType = 'Consumos';

                                            return (
                                                <React.Fragment key={'g-group-'+group.key}>
                                                    <tr style={{background: 'var(--bs-gray-200)'}} className="fw-bold">
                                                        <td style={{paddingLeft: '20px'}}>
                                                            <div className="d-flex justify-content-between">
                                                                {group.value.name}
                                                                <span className="text-uppercase badge bg-dark d-flex align-items-center">{distributionType}</span>
                                                            </div>
                                                        </td>
                                                        <td>{formatNumber(group.value.amount ?? 0, 2)} €</td>
                                                    </tr>
                                                    {jsonToArray(group?.value.titles)?.map(title => {
                                                        return (
                                                            <React.Fragment key={'g-title-'+title.key}>
                                                                <tr style={{background: '#F6993F'}} className="fw-bold">
                                                                    <td style={{paddingLeft: '40px'}}>{title.value.name}</td>
                                                                    <td>{formatNumber(title.value.amount ?? 0, 2)} €</td>
                                                                </tr>
                                                                {jsonToArray(title?.value.expenses)?.map(expenses => {
                                                                    return (
                                                                        <React.Fragment key={'g-expenses-'+expenses.key}>
                                                                            <tr style={{background: '#f8f9fa73'}} className="fw-bold">
                                                                                <td style={{paddingLeft: '60px'}}>{expenses.value.name}</td>
                                                                                <td>{formatNumber(expenses.value.amount ?? 0, 2)} €</td>
                                                                            </tr>
                                                                            {expenses?.value?.movements?.map(movement => {
                                                                                return (
                                                                                    <React.Fragment key={'g-expenses-movements'+movement.key}>
                                                                                        <tr>
                                                                                            <td style={{paddingLeft: '80px'}}>
                                                                                                <span className="badge bg-light text-secondary">{movement.type === 'providerinvoice' && 'F'}{movement.type === 'counterpart' && 'C'}</span>&nbsp;
                                                                                                {movement.name}
                                                                                            </td>
                                                                                            <td>{formatNumber(movement.amount ?? 0, 2)} €</td>
                                                                                        </tr>
                                                                                    </React.Fragment>
                                                                                );
                                                                            })}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </CounterpartsStyled>
            }
        </>
    );
};

const ExportBtn = (props) => {
    const {year} = props;
    
    const exportClick = async () => {
        await axios.post('api/manager/balances/export/counterparts', {
            community_id: getCommunity()?.id,
            financial_year: year
        }, {
            responseType: 'blob',
            cancelToken: axiosCancelToken.token
        }).then((response) => {
            let fileName = response.headers.file_name;

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentElement.removeChild(link);
        }).catch((error) => {
            if ( axios.isCancel(error) ) return;
        });
    }

    return (
        <button className="btn btn-sm btn-light ms-2" onClick={exportClick}>Exportar</button>    
    );
}